package top.wcpe.allincore.dsl.ir.expr

import kotlin.jvm.JvmStatic

/**
 * D2-b 引入的 runtime 端类型枚举。
 *
 * 与 dsl-compiler 的 `IrType` 在 wire 编号（ordinal）层面保持一致：
 * 状态机 wire format 以 ordinal 持久化 var 类型槽位。
 *
 * 顺序与编译期 IrType 持久化约定：
 * 0=NUMBER, 1=STRING, 2=BOOLEAN, 3=MAP, 4=LIST, 5=ANY, 6=NULLABLE_ANY,
 * 7=NULLABLE_NUMBER, 8=NULLABLE_STRING, 9=NULLABLE_BOOLEAN, 10=NULLABLE_MAP, 11=NULLABLE_LIST。
 *
 * Nullable 语义参照 Kotlin：`String?` 表示该字段可接受 null 或 String，
 * 赋值时同时检查 null 放行和非 null 值的 inner 类型。
 *
 * 里程碑 0 KMP spike：本枚举从原 Java 版平移到 commonMain（纯 Kotlin），
 * 语义、常量名、序数与静态方法完全等价，供 jvmMain 的 Java 代码互操作调用。
 */
enum class IrType {
    /** 数值（IR 层 Double）。 */
    NUMBER,
    /** 字符串。 */
    STRING,
    /** 布尔。 */
    BOOLEAN,
    /** 字典（Map）。 */
    MAP,
    /** 列表（List）。 */
    LIST,
    /** 未知 / 动态。 */
    ANY,
    /** 可空（兜底，inner 类型未知时使用；保留 ordinal=6 兼容旧 wire 数据）。 */
    NULLABLE_ANY,
    /** 可空数值（Number?）。 */
    NULLABLE_NUMBER,
    /** 可空字符串（String?）。 */
    NULLABLE_STRING,
    /** 可空布尔（Boolean?）。 */
    NULLABLE_BOOLEAN,
    /** 可空字典（Map?）。 */
    NULLABLE_MAP,
    /** 可空列表（List?）。 */
    NULLABLE_LIST;

    /** 判断此类型是否允许 null 值。 */
    fun isNullable(): Boolean = ordinal >= NULLABLE_ANY.ordinal

    companion object {
        /** 缓存的枚举值数组，避免每次反查都新建。 */
        private val VALUES = values()

        /**
         * 按 wire ordinal 反查；越界回退 ANY。
         *
         * 前向兼容：v16 新增 ordinal 7..11（NULLABLE_NUMBER 等），旧 reader（v15 及以前）
         * 不认识这些 ordinal 时会走此方法的越界分支返回 ANY——语义降级但不崩溃。
         */
        @JvmStatic
        fun fromOrdinal(ord: Int): IrType {
            if (ord < 0 || ord >= VALUES.size) return ANY
            return VALUES[ord]
        }
    }
}
